import type { GetOtpSendStatus200 } from '@api/models';

import type { OtpErrorCode } from '@domain/models/otp';
import type { OtpChannel, OtpDeliveryInfo } from '@ui/components/features/otp';
import { ApiError } from '@website/lib/api/browserFetch';

export type OtpSendErrorKey =
  | 'rateLimited'
  | 'invalidNumber'
  | 'smsUnsupported'
  | 'numberBlocked'
  | 'callNeedsSms'
  | 'sendFailed';
export type OtpVerifyErrorKey = 'expired' | 'tooManyAttempts' | 'rateLimited' | 'invalidCode';

const SEND_ERROR_KEYS: Partial<Record<OtpErrorCode, OtpSendErrorKey>> = {
  rate_limited: 'rateLimited',
  invalid_number: 'invalidNumber',
  sms_unsupported: 'smsUnsupported',
  number_blocked: 'numberBlocked',
  call_needs_sms: 'callNeedsSms'
};

const VERIFY_ERROR_KEYS: Partial<Record<OtpErrorCode, OtpVerifyErrorKey>> = {
  code_expired: 'expired',
  too_many_attempts: 'tooManyAttempts'
};

/** The API's OTP error code, or `rate_limited` for a gateway 429. */
function otpErrorCode(error: unknown): OtpErrorCode | undefined {
  if (!(error instanceof ApiError)) return undefined;
  if (error.statusCode === 429) return 'rate_limited';
  return error.code as OtpErrorCode | undefined;
}

/** Maps a failed `POST /otp/send` to the `Otp.*` message key shown in the modal. */
export function getOtpSendErrorKey(error: unknown): OtpSendErrorKey {
  const code = otpErrorCode(error);
  return (code && SEND_ERROR_KEYS[code]) || 'sendFailed';
}

/**
 * Maps a thrown `POST /otp/verify` error to the `Otp.*` message key shown in the modal.
 * A wrong code is not an error (the API answers `{ verified: false }`).
 */
export function getOtpVerifyErrorKey(error: unknown): OtpVerifyErrorKey {
  const code = otpErrorCode(error);
  if (code === 'rate_limited') return 'rateLimited';
  return (code && VERIFY_ERROR_KEYS[code]) || 'invalidCode';
}

/** A 4xx from the status endpoint (e.g. 404) will not change on retry. */
export function isFinalOtpStatusError(error: unknown): boolean {
  return error instanceof ApiError && error.statusCode >= 400 && error.statusCode < 500 && error.statusCode !== 429;
}

export interface OtpDeliveryState {
  /** Channel of the latest send, the one `sendId` and `status` belong to. */
  channel: OtpChannel;
  isSending: boolean;
  sendFailed: boolean;
  sendId: string | null;
  status: GetOtpSendStatus200 | undefined;
  /** Polling gave up (time limit or a final error) without a carrier answer. */
  statusUnavailable: boolean;
}

/** Nothing to show before a send, or when the send itself failed (its error says why). */
export function getOtpDeliveryInfo({
  channel,
  isSending,
  sendFailed,
  sendId,
  status,
  statusUnavailable
}: OtpDeliveryState): OtpDeliveryInfo | undefined {
  if (isSending) return { channel, status: 'sending' };
  if (sendFailed || !sendId) return undefined;
  if (status && status.status !== 'pending') {
    return {
      channel,
      status: status.status,
      reason: status.reason,
      carrier: status.carrier,
      deliveredAt: status.deliveredAt
    };
  }
  return { channel, status: statusUnavailable ? 'unknown' : 'pending' };
}
